import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import { useMediaHitsCardExperiment } from '../hooks/useMediaHitsCardExperiment';

import type { MediaHitResolution, MediaHitResolver } from './resolvers';
import { resolveMediaHitCard, resolveMediaHitEmbed } from './resolvers';
import type { MediaHitEmbed, MediaHitsContextValue } from './types';
import { toLoadingEmbed } from './types';

/** Keeps the state update out of a nested ternary inside the `.map()` at the call site. */
const toEmbedPatch = (resolution: MediaHitResolution): Pick<MediaHitEmbed, 'status' | 'html' | 'card'> =>
	resolution.status === 'valid'
		? { status: 'valid', html: resolution.html, card: resolution.card }
		: { status: 'invalid', html: '', card: null };

// oxlint-disable-next-line max-lines-per-function
export function useMediaHitsContextValue(): MediaHitsContextValue {
	const [mediaEmbeds, setMediaEmbeds] = useState<MediaHitEmbed[]>([]);
	const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | undefined>();
	// Tracks which `serverMediaHits` set we've already started embed fetches for, keyed by reference (the page-data
	// array is stable per petition). Keyed rather than a boolean so a new dataset is always (re)loaded — including a
	// client-side navigation to another petition where this provider stays mounted. A boolean guard reset in the
	// seeding effect would be reset too late: the consuming section's effect runs (child-first) before this one.
	const loadedFor = useRef<unknown>(null);

	const {
		data: {
			petition: { id: petitionId },
			mediaHitsPage: { mediaHits: serverMediaHits },
		},
	} = usePetitionDetailsPageData();

	const utilityContext = useUtilityContext();
	const { track } = useCampaignTracking();
	const { showCard } = useMediaHitsCardExperiment();

	// Seed the embeds (in a loading status) from the server data. Embeds are not fetched here — that is deferred to
	// `loadEmbeds`, which the section triggers once it nears the viewport. On a dataset change (client-side navigation
	// to another petition, or a page-data refresh) this reseeds to `loading`; `loadEmbeds` re-loads on its own because
	// its guard is keyed to the dataset (see `loadedFor`).
	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setMediaEmbeds(serverMediaHits.map(toLoadingEmbed));
	}, [serverMediaHits]);

	// Each arm needs different data from a different iframely endpoint, and neither uses the other's:
	// control renders the embed markup, treatment renders our own card from the metadata. Picking a
	// resolver (rather than branching inside the fetch) keeps this hook free of arm-specific logic.
	const resolveMediaHit: MediaHitResolver = showCard ? resolveMediaHitCard : resolveMediaHitEmbed;

	const resolveEmbed = useCallback(
		async ({ id, url }: { id: string; url: string }) => {
			// No try/catch here on purpose: resolvers never reject, they return `{ status: 'invalid' }`.
			const resolution = await resolveMediaHit({ url, utilityContext });
			const patch = toEmbedPatch(resolution);

			// A hit that can't be resolved is marked invalid on its own; the rest of the list is unaffected.
			setMediaEmbeds((curState) => curState.map((embed) => (embed.id === id ? { ...embed, ...patch } : embed)));

			if (resolution.status === 'invalid') {
				// Fired here rather than inside the resolvers: passing them `track` and `petitionId` would
				// couple fetching to tracking, and duplicating the call is exactly where the two arms could
				// drift apart unnoticed.
				track('media_hit_iframely_embed_failure', { petition_id: petitionId, media_url: url });
			}
		},
		[resolveMediaHit, utilityContext, track, petitionId],
	);

	const loadEmbeds = useCallback(() => {
		// Skip only if we've already started loading *this* dataset; a new `serverMediaHits` always (re)loads.
		if (loadedFor.current === serverMediaHits) return;
		loadedFor.current = serverMediaHits;
		// Fire each embed fetch independently (no `Promise.all` gating) so the list isn't held on the slowest one.
		serverMediaHits.forEach((mediaHit) => void resolveEmbed(mediaHit));
	}, [serverMediaHits, resolveEmbed]);

	const removeMedia = useCallback((mediaId: string) => {
		setMediaEmbeds((curState) => curState.filter((embed) => embed.id !== mediaId));
	}, []);

	const addMedia = useCallback(
		(id: string, url: string) => {
			setMediaEmbeds((curState) => [toLoadingEmbed({ id, url, mediaSource: 'STAFF_ADDED' }), ...curState]);
			void resolveEmbed({ id, url });
		},
		[resolveEmbed],
	);

	return useMemo(
		() => ({
			data: {
				mediaEmbeds,
				submitStatus,
			},
			actions: {
				loadEmbeds,
				addMedia,
				removeMedia,
				setSubmitStatus,
			},
		}),
		[mediaEmbeds, submitStatus, loadEmbeds, addMedia, removeMedia, setSubmitStatus],
	);
}
